<script setup lang="ts">
// ================================
// PRÉSENTATION — modification adresse bâtiment (équivalent legacy FormBuildingModifyAddress)
// ================================
import { computed, inject, onMounted, ref, watch } from "vue"
import { useRouter, useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/yup"
import * as yup from "yup"
import { useToast } from "primevue/usetoast"
import type { Address } from "@types"
import type { Services } from "@services"
import type { ParcelLocationMultiPolygon } from "@core/services/building.service"

/**
 * Bâtiment pour modification d'adresse (format API ou legacy avec geometry)
 */
export interface BuildingModifyAddress {
    _id?: string
    _id_building?: string
    address?: Address
    level?: string
    infos?: { niveau?: string }
    geometry?: {
        address?: { coordinates?: [number, number] }
        cadastre?: {
            id_building?: string
            numero_parcelle?: string
            location?: unknown
        }
    }
    location?: { coordinates?: number[] }
}

export interface Props {
    /** Bâtiment dont on modifie l'adresse */
    building: BuildingModifyAddress
}

interface Emits {
    (e: "update:finish", success?: boolean): void
    (e: "click:cancel"): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n({
    messages: {
        fr: {
            warningTitle: "Informations importantes",
            warningMessage:
                'En changeant votre adresse, vous pouvez redéfinir la parcelle associée et la catégorie de bâtiment souhaitée. Attention le changement d\'adresse entraînera la suppression des liens avec les autres bâtiments. Vous pourrez recréer ces liens par la suite dans la page "Info. du bâtiment".',
            headingAddress: "Adresse",
            headingLevel: "Niveau",
            headingParcel: "Parcelle cadastrale",
            cancel: "Annuler",
            modify: "Modifier",
            successUpdate: "L'adresse du bâtiment a été mise à jour.",
            validation: {
                addressRequired: "L'adresse est obligatoire",
                levelRequired: "Le niveau est obligatoire",
                levelOneOf:
                    "Le niveau doit être l'un des suivants : groupe, immeuble, appartement",
                parcelRequired:
                    "Sélectionnez une parcelle ou indiquez que vous l’ignorez",
            },
        },
    },
})

const services = inject<Services>("services")
const router = useRouter()
const route = useRoute()
const toast = useToast()

const validationSchema = computed(() =>
    toTypedSchema(
        yup.object({
            address: yup
                .mixed<Address>()
                .required(t("validation.addressRequired"))
                .test(
                    "addr-ok",
                    t("validation.addressRequired"),
                    (val) =>
                        !!val &&
                        typeof val === "object" &&
                        !!(val as Address).inline_name?.trim()
                ),
            level: yup
                .string()
                .required(t("validation.levelRequired"))
                .oneOf(
                    ["groupe", "immeuble", "appartement"],
                    t("validation.levelOneOf")
                ),
        })
    )
)

const { handleSubmit, errors, defineField, setFieldValue } = useForm({
    validationSchema,
    initialValues: {
        address: null as Address | null,
        level: "",
    },
})

const [addressField] = defineField("address")
const [levelField] = defineField("level")

const modelParcel = ref<{ parcelId: string; parcelGeometry: unknown }>({
    parcelId: "",
    parcelGeometry: null,
})
const skipParcel = ref(false)
const location = ref<[number, number] | null>(null)
const submitState = ref<"OK" | "PENDING" | "ERROR">("OK")
const submitError = ref("")

const getBuildingLevel = computed(
    () => props.building?.level ?? props.building?.infos?.niveau ?? ""
)

const getBuildingId = computed(
    () => props.building._id_building ?? props.building._id ?? ""
)

/**
 * Géométrie carte → MultiPolygon attendu par l’API (validation / address).
 */
function toParcelLocationPayload(
    geometry: unknown
): ParcelLocationMultiPolygon | undefined {
    if (!geometry || typeof geometry !== "object") return undefined
    const g = geometry as { type?: string; coordinates?: unknown }
    if (g.type === "MultiPolygon" && Array.isArray(g.coordinates)) {
        return {
            type: "MultiPolygon",
            coordinates: g.coordinates as number[][][][],
        }
    }
    if (g.type === "Polygon" && Array.isArray(g.coordinates)) {
        return {
            type: "MultiPolygon",
            coordinates: [g.coordinates as number[][][]],
        }
    }
    return undefined
}

function initFromBuilding(): void {
    setFieldValue("address", props.building?.address ?? null)
    const coords =
        props.building?.geometry?.address?.coordinates ??
        props.building?.location?.coordinates
    if (Array.isArray(coords) && coords.length >= 2) {
        location.value = [coords[0], coords[1]]
    } else {
        location.value = null
    }
    const lvl = ["immeuble", "appartement"].includes(getBuildingLevel.value)
        ? getBuildingLevel.value
        : ""
    setFieldValue("level", lvl)
    const cadastre = props.building?.geometry?.cadastre
    modelParcel.value = {
        parcelId: cadastre?.id_building ?? cadastre?.numero_parcelle ?? "",
        parcelGeometry: cadastre?.location ?? null,
    }
}

onMounted(() => {
    initFromBuilding()
})

watch(
    () => props.building,
    () => initFromBuilding(),
    { deep: true }
)

function onUpdateLocation(value: [number, number] | null): void {
    location.value = value
}

function onUpdateLevel(value: string): void {
    setFieldValue("level", value)
    if (value === "groupe") {
        modelParcel.value = { parcelId: "", parcelGeometry: null }
    }
}

function onUpdateParcel(value: {
    parcelId: string
    parcelGeometry: unknown
}): void {
    modelParcel.value = value
}

function onUpdateSkipParcel(value: boolean): void {
    skipParcel.value = value
}

function onClickCancel(): void {
    emit("click:cancel")
}

const onClickModifyAddress = handleSubmit(async (values) => {
    const buildingId = getBuildingId.value
    const addr = values.address as Address
    const lvl = values.level
    if (!buildingId || !addr || !lvl) return
    if (
        ["immeuble", "appartement"].includes(lvl) &&
        !skipParcel.value &&
        !modelParcel.value.parcelId?.trim()
    ) {
        submitError.value = t("validation.parcelRequired")
        submitState.value = "ERROR"
        return
    }
    try {
        submitState.value = "PENDING"
        submitError.value = ""
        const idBuilding = modelParcel.value?.parcelId?.trim() || undefined
        const parcelLocation = toParcelLocationPayload(
            modelParcel.value?.parcelGeometry
        )
        await services!.$updateBuildingAddress(buildingId, {
            address: addr,
            level: lvl,
            id_building: idBuilding,
            ...(idBuilding && parcelLocation
                ? { parcel_location: parcelLocation }
                : {}),
        })
        submitState.value = "OK"
        toast.add({
            severity: "success",
            summary: t("successUpdate"),
            life: 3000,
        })
        emit("update:finish", true)
        setTimeout(() => {
            router.push({ path: route.path })
        }, 500)
    } catch (err) {
        submitState.value = "ERROR"
        submitError.value =
            (err as Error)?.message ?? "Erreur lors de la mise à jour"
        toast.add({
            severity: "error",
            summary: submitError.value,
            life: 5000,
        })
    }
})
</script>

<template>
    <div
        class="building-update-address-form flex flex-col justify-center items-center gap-6 w-full max-w-2xl"
    >
        <BaseMessage severity="warn" :closable="false" class="w-full">
            <div class="flex flex-col gap-2">
                <span class="font-semibold">{{ t("warningTitle") }}</span>
                <p>{{ t("warningMessage") }}</p>
            </div>
        </BaseMessage>

        <div class="building-update-address-form__section w-full">
            <h3 class="text-base font-medium text-surface-800 mb-2">
                {{ t("headingAddress") }}
            </h3>
            <InputAddress
                :model-value="addressField"
                class="w-full"
                data-cy="address-input"
                @update:model-value="
                    (v: Address | null) => setFieldValue('address', v)
                "
                @update:coordinates="onUpdateLocation"
            />
            <p
                v-if="errors.address"
                class="text-sm text-red-600 mt-1 text-center"
            >
                {{ errors.address }}
            </p>
        </div>

        <div
            v-if="addressField"
            class="building-update-address-form__section w-full"
        >
            <h3 class="text-base font-medium text-surface-800 mb-2">
                {{ t("headingLevel") }}
            </h3>
            <BuildingFormBoxListLevel
                :model-value="levelField"
                :only="['groupe', 'immeuble', 'appartement']"
                @update:model-value="onUpdateLevel"
            />
            <p
                v-if="errors.level"
                class="text-sm text-red-600 mt-1 text-center"
            >
                {{ errors.level }}
            </p>
        </div>

        <div
            v-if="
                addressField && ['immeuble', 'appartement'].includes(levelField)
            "
            class="building-update-address-form__section w-full"
        >
            <h3 class="text-base font-medium text-surface-800 mb-2">
                {{ t("headingParcel") }}
            </h3>
            <BuildingFormParcelMap
                :model-parcel="modelParcel"
                :model-skip-parcel="skipParcel"
                :address-inline="addressField?.inline_name ?? ''"
                :address-location="location"
                disable-enedis-network
                @update:model-parcel="onUpdateParcel"
                @update:model-skip-parcel="onUpdateSkipParcel"
            />
        </div>

        <footer
            class="building-update-address-form__footer flex justify-center items-center gap-4 flex-wrap w-full pt-6"
        >
            <BaseButton
                :label="t('cancel')"
                severity="secondary"
                @click="onClickCancel"
            />
            <BaseButton
                :label="t('modify')"
                :loading="submitState === 'PENDING'"
                @click="onClickModifyAddress"
            />
        </footer>

        <BaseMessage
            v-if="submitState === 'ERROR'"
            class="w-full"
            severity="error"
            :closable="false"
        >
            {{ submitError }}
        </BaseMessage>
    </div>
</template>
